import { Injectable, OnDestroy, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { Subscription, ReplaySubject } from 'rxjs';
import { takeUntil, pairwise, filter } from 'rxjs/operators';

import { LaunchDarklyFlags, ShareScreenVideoCodec } from 'app/core/services/launch-darkly/launch-darkly.types';
import { coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import { ModalErrorComponent, ModalErrorData } from 'app/shared/modal-error/modal-error.component';
import assertDefined from 'app/utils/assert-defined';
import { Loggable } from 'app/utils/logging/loggable';
import { filterCodecs } from 'app/utils/media/rtc';
import { cleanTracks } from 'app/utils/media/stream';
import { isUndefined } from 'app/utils/stream-util';
import User from 'entity/User';
import {
  ScreenShare,
  Presenter,
  SharingPublishCandidateMessage,
  OutgoingMessageType,
  PeerCandidate,
  StartSharingMessage,
  IncomingMessageType,
  ScreenShareStatus,
  EndSharingMessage,
  MirrorType,
  StartMirrorMessage,
  EndMirrorMessage,
  StartRecordingMessage,
  RecordingType,
  EndRecordingMessage,
  UpdateType,
  IncomingMessage,
  PresenterType,
} from 'messages/websocket.messages';

import SocialStateMachine from '../../social-state-machine/social-state-machine';
import { socialAppSelectors } from '../../state/app';
import { RESOLUTION_1080P, RESOLUTION_STANDARD } from '../../types/media';
import { BrowserService } from '../browser.service';
import { createMirrorId } from '../presentation/util';
import { PublisherType, RtcStatsStreamService, StatsCounter } from '../rtc-stats-stream';
import { processScreenShareError } from '../share-screen-helpers/share-screen-error.helper';
import { VideoRoomService } from '../video-room.service';
import { WebsocketService } from '../websocket.service';

@Injectable({
  providedIn: 'root',
})
export class ShareScreenService extends Loggable implements OnDestroy {
  private dialog = inject(MatDialog);
  private videoRoomService = inject(VideoRoomService);
  private websocketService = inject(WebsocketService);
  private rtcStatsStreamService = inject(RtcStatsStreamService);
  private store = inject(Store);
  private browserService = inject(BrowserService);

  public settings = {
    basic: {
      frameRate: 16,
      bitrate: RESOLUTION_STANDARD.bitrate,
      hasAudio: false,
    },
    enhanced: {
      frameRate: 30,
      bitrate: RESOLUTION_1080P.bitrate,
      hasAudio: true,
    },
  };

  private screenStream?: MediaStream;
  private user?: User;

  // WebRTC Peer connection
  private publisherCandidates: SharingPublishCandidateMessage[] = [];
  private _peer?: RTCPeerConnection;

  // Track peer connection start time (unix milliseconds)
  private peerConnectionTime?: number;

  public sharingInProgress = false;
  private screenShareStatus = ScreenShareStatus.NONE;
  private isPresentationActive = false;

  private mirrorId?: string;
  private mirrorSocialId?: string;
  private isCrossRoomPresentation = false;
  private isGuest = false;
  private helpLinkScreenShareError?: string;
  private recordingId?: string;
  private socialId = '';
  private websocketSubscription?: Subscription;
  private screenShare?: ScreenShare;
  private destroyed$ = new ReplaySubject<boolean>(1);

  private codecSetting: ShareScreenVideoCodec = { mimeType: '', sdpFormatFilter: '' };
  protected enhanced = false;

  public constructor() {
    super();
    this.setDisplayName('ScreenShareService');

    // User state
    this.store
      .select(coreSelectors.getUser)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ isGuest, user }) => {
        this.user = user;
        this.isGuest = isGuest;
      });

    // Social state
    this.store
      .select(socialAppSelectors.getJoinedSocialId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((socialId) => (this.socialId = socialId ?? ''));

    // Presentation state
    this.store
      .select(socialAppSelectors.isCrossRoomPresentation)
      .subscribe((isCrossRoomPresentation) => (this.isCrossRoomPresentation = isCrossRoomPresentation));

    this.store
      .select(socialAppSelectors.isPresentationActive)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPresentationActive) => (this.isPresentationActive = isPresentationActive));

    this.store
      .select(socialAppSelectors.getScreenShareState)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ status }) => (this.screenShareStatus = status));

    // Recording state
    this.store.select(socialAppSelectors.getRecordingId).subscribe((recordingId) => (this.recordingId = recordingId));
    // Start and stop recording of the local presenter's publisher stream
    this.store
      .select(socialAppSelectors.isUserSharingScreenAndRecorded)
      .pipe(
        pairwise(),
        filter(([oldRecording, newRecording]) => oldRecording !== newRecording),
      )
      .subscribe(([_, recording]) => {
        if (recording) {
          this.startScreenShareRecording();
        } else {
          this.endScreenShareRecording();
        }
      });

    // Initiate mirror when this user is cross-room screen sharing and a mirror session is not already active
    this.store
      .select(socialAppSelectors.isUserCrossRoomScreenSharing)
      .pipe(
        filter((isCrossRoom) => this.mirrorId === undefined && isCrossRoom),
        takeUntil(this.destroyed$),
      )
      .subscribe(this.startMirror.bind(this));

    // End mirror when presentation changes from cross-room to local and user is still presenting
    this.store
      .select(socialAppSelectors.getCrossRoomPresentationStatus)
      .pipe(pairwise())
      .subscribe(([prevCrossRoomStatus, crossRoomStatus]) => {
        if (prevCrossRoomStatus.isCrossRoom && !crossRoomStatus.isCrossRoom && crossRoomStatus.isPresenting) {
          this.endMirror();
        }
      });

    this.store
      .select(socialAppSelectors.getScreenShareState)
      .subscribe(({ screenShare }) => (this.screenShare = screenShare));

    // End Screen share if inactive presentation
    this.store
      .select(socialAppSelectors.isScreenShareActiveWithoutPresentation)
      .pipe(filter((isActive) => isActive))
      .subscribe({
        next: async () => {
          await this.stopSharing().catch((e) => this.error('Error stop sharing', e));
        },
      });

    this.store
      .select(socialAppSelectors.getPresenterIds)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: async (ids) => {
          const userId = this.user?.userId ?? '';
          if (!ids.includes(userId) && this.sharingInProgress) {
            await this.stopSharing().catch((e) => this.error('Error stop sharing', e));
          }
        },
      });

    this.store
      .select(socialAppSelectors.isEventActive)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: async (active) => {
          if (!active && this.sharingInProgress) {
            await this.stopSharing().catch((e) => this.error('Error stop sharing', e));
          }
        },
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.share_screen_video_codec)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((codecSetting) => (this.codecSetting = codecSetting));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.help_link_screen_share_error)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((url) => (this.helpLinkScreenShareError = url));

    this.settings.basic.bitrate = this.videoRoomService.getSvcSettings()?.bitrate || RESOLUTION_STANDARD.bitrate;
  }

  // Websocket Message Listener
  private wsMessageListener = async (msg: IncomingMessage): Promise<void> => {
    const { t } = msg;
    switch (t) {
      case IncomingMessageType.SHR_PUBANS: {
        if (!this.peer) {
          return;
        }
        // After publish add screen share to state
        await this.addScreenShareToState();

        const { d } = msg;
        await this.peer.setRemoteDescription(d as RTCSessionDescriptionInit);
        return;
      }
      case IncomingMessageType.SHR_ENDED: {
        await this.stopSharing();
        return;
      }
    }
  };

  /**
   * Start a mirror session
   */
  private startMirror = (): void => {
    const type = MirrorType.SCREEN_SHARE;
    if (!this.user) {
      this.error('Attempted to start mirror with no user');
      return;
    }
    const mirrorId = createMirrorId(this.user, type);
    const socialId = this.getMirrorSocialId();

    this.info('Creating mirror session', { mirrorId, type });
    try {
      const msg: StartMirrorMessage = {
        t: OutgoingMessageType.START_MIRROR,
        mirrorId,
        type,
        socialId,
      };

      this.websocketService.sendMessage(msg);
    } catch (e) {
      const { message } = e as { message: string };
      this.error('Failed to start mirror session:', message);
    }

    this.mirrorId = mirrorId;
    this.mirrorSocialId = socialId;
  };

  /**
   * End active mirror sessions
   */
  private endMirror(): void {
    if (!this.mirrorId) {
      this.warn('Attempted to end mirror with no mirror id');
      return;
    }

    this.info('Ending mirror session', { mirrorId: this.mirrorId });

    try {
      const msg: EndMirrorMessage = {
        t: OutgoingMessageType.END_MIRROR,
        mirrorId: this.mirrorId,
        type: MirrorType.SCREEN_SHARE,
        socialId: this.mirrorSocialId,
      };
      this.websocketService.sendMessage(msg);
    } catch (e) {
      const { message } = e as { message: string };
      this.error('Failed to end mirror session:', message);
    }

    this.mirrorId = undefined;
    this.mirrorSocialId = undefined;
  }

  private getMirrorSocialId(): string {
    const socialId = this.screenShare?.presenter?.socialId ?? '';
    return socialId;
  }

  /**
   * Initiate screen share
   *
   * @returns Boolean - true if screen share started
   */
  public async startSharing(enhanced = false): Promise<boolean> {
    if (!this.user) {
      this.fatal('Cannot screen share without a user set');
      return false;
    }
    assertDefined(this.user.userId);

    this.enhanced = enhanced;

    this.info('Starting screen share');

    // Init Websocket Message Listener
    this.websocketSubscription = this.websocketService.message$.subscribe(this.wsMessageListener.bind(this));

    // Request Screen Share Stream
    try {
      this.screenStream = await navigator.mediaDevices.getDisplayMedia({
        //enhanced  ?
        video: {
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        // : true,
        audio: enhanced,
      });
    } catch (error) {
      this.warn('Denied permissions to share screen', error);
      this.showError(processScreenShareError(error));
      await this.stopSharing();
      return false;
    }

    // Do not initiate if you are already watching someone else's screen
    if (this.screenShareStatus === ScreenShareStatus.ACTIVE) {
      this.info('Ending screen share: share already active');
      this.showError('Another screen share is already in progress. Only one screen share may be active at a time.');
      await this.stopSharing();
      return false;
    }

    // Notify Status
    this.sharingInProgress = true;

    const frameRate = enhanced ? this.settings.enhanced.frameRate : this.settings.basic.frameRate;

    // Stream Configurations
    if (this.screenStream) {
      this.setFrameRate(this.screenStream, frameRate);
      this.addStreamStopListeners(this.screenStream);
      await this.createScreenSharePeer(this.screenStream);
    }

    return true;
  }

  private async addScreenShareToState(updateTypeRequest = UpdateType.CURRENT): Promise<void> {
    if (!this.user) {
      this.fatal('Cannot screen share without a user set');
      return;
    }
    assertDefined(this.user.userId);
    const { firstName, lastName, userId } = this.user;
    const { isGuest, socialId } = this;
    const presenterType = PresenterType.SCREEN_SHARE;
    const presenter: Presenter = { userId, firstName, lastName, isGuest, socialId, presenterType };

    let updateType = UpdateType.CURRENT;
    if (this.isPresentationActive) {
      updateType = this.isCrossRoomPresentation ? UpdateType.ALL : UpdateType.CURRENT;
    } else {
      // old behaviour, crashing state
      // updateType = this.isConnectedRoom ? UpdateType.ALL : UpdateType.CURRENT;
      updateType = updateTypeRequest;
    }
    const isGlobal = updateType === UpdateType.ALL ? true : false;
    const screenShare: ScreenShare = { presenter, groupId: '', isGlobal: isGlobal };

    await SocialStateMachine.shared
      .requestStartScreenShare({
        screenShare,
        updateType,
        userId,
      })
      .catch((e) => this.error('Failed to start screen share', e));
  }

  /**
   * Show an error modal to the user.
   * @param text The error text to display
   */
  private showError(text: string): void {
    const helpLink = this.helpLinkScreenShareError
      ? { url: this.helpLinkScreenShareError, text: 'Understanding screen share errors' }
      : undefined;
    const message = {
      title: 'Unable to share your screen',
      text: text,
      helpLink,
    };
    this.dialog.open<ModalErrorComponent, ModalErrorData>(ModalErrorComponent, {
      data: message,
    });
  }

  private setFrameRate(stream: MediaStream, frameRate: number): void {
    stream.getTracks().forEach(async (track) => {
      const constraints = track.getConstraints();
      constraints.frameRate = frameRate;
      try {
        await track.applyConstraints(constraints);
      } catch (error) {
        if (error instanceof OverconstrainedError) {
          this.warn(`Unable to set frame rate to ${constraints.frameRate}. Using default frame rate.`);
        } else {
          throw error;
        }
      }
    });
  }

  private addStreamStopListeners(stream: MediaStream): void {
    stream.addEventListener('ended', this.stopSharing.bind(this), false);
    stream.addEventListener('inactive', this.stopSharing.bind(this), false);

    if (this.browserService.isSafari || this.browserService.isFirefox) {
      const videoTrack = stream.getVideoTracks()[0];
      videoTrack.addEventListener('ended', this.stopSharing.bind(this), false);
    }
  }

  public async stopSharing(): Promise<void> {
    if (this.mirrorId) {
      this.endMirror();
    }

    // only presenter can end screen share
    if (this.user?.userId !== this.screenShare?.presenter?.userId) {
      return;
    }

    // Set State sharing screen >> FALSE
    if (this.screenShareStatus === ScreenShareStatus.ACTIVE) {
      this.sharingInProgress = false;
      // host needs to be able to end all screen shares, not just their own
      const userId = this.screenShare?.presenter?.userId ?? '';

      await SocialStateMachine.shared
        .requestEndScreenShare({
          presenterId: userId,
          userId,
          updateType: this.isCrossRoomPresentation ? UpdateType.ALL : UpdateType.CURRENT,
        })
        .catch((e) => this.error('Failed to end screen share', e));

      if (this.socialId) {
        const shareEnd: EndSharingMessage = {
          t: OutgoingMessageType.SHREND,
          socialId: this.socialId,
        };
        this.websocketService.sendMessage(shareEnd);
      }
    }

    this.websocketSubscription?.unsubscribe();
    this.websocketSubscription = undefined;

    // Destroy Transmission
    this.peer?.close();
    this.peer = undefined;

    await cleanTracks(this.screenStream, this);
    this.stopPictureInPicture();
  }
  /**
   * Start recording of the screen share stream
   *
   * Recording is toggled for the room globally, but each client is responsible for initiating the recording of their own stream.
   */
  private startScreenShareRecording() {
    if (!this.recordingId) {
      this.fatal('startScreenShareRecording called without a recordingId');
      return;
    }
    this.info('Starting screen share recording');
    const msg: StartRecordingMessage = {
      t: OutgoingMessageType.START_RECORDING,
      recordingId: this.recordingId,
      type: RecordingType.SCREEN_SHARE,
    };
    this.websocketService.sendMessage(msg);
  }

  /**
   * Stop recording of the screen share stream
   *
   * Recording is toggled for the room globally, but each client is responsible for initiating the recording of their own stream.
   */
  private endScreenShareRecording() {
    this.info('Stopping screen share recording');
    const msg: EndRecordingMessage = {
      t: OutgoingMessageType.END_RECORDING,
      type: RecordingType.SCREEN_SHARE,
    };
    this.websocketService.sendMessage(msg);
  }

  public async updateScreenShare(crossRoom: boolean): Promise<void> {
    if (!this.user) {
      this.error('updateScreenShare called without a user');
      return;
    }

    if (!this.sharingInProgress) {
      this.error('updateScreenShare called when sharing not in progress');
      return;
    }

    if (!this.screenShare) {
      this.error('updateScreenShare screen share is undefined');
      return;
    }

    assertDefined(this.user.userId);

    if (crossRoom) {
      this.info('updateScreenShare: Adding screen share to other rooms');
      await SocialStateMachine.shared
        .requestUpdateScreenShare({
          screenShare: this.screenShare,
          updateType: UpdateType.OTHERS,
          userId: this.user?.userId,
        })
        .catch((e) => this.error('Failed to start screen share', e));
    }
  }
  // -- WebRTC Transmission --------------------------------------------------------------------------------------

  private async createScreenSharePeer(stream: MediaStream): Promise<void> {
    this.publisherCandidates = [];
    const rtcConfig = this.videoRoomService.createRtcConfig();

    // Setup WebRTC Peer and Tracks
    this.peer = new RTCPeerConnection(rtcConfig);
    this.peerConnectionTime = Date.now();

    this.peer.onicecandidate = (event) => this.publishScreenShareCandidate(event);
    this.peer.onicecandidateerror = (e) => this.trace('Candidate error', e);
    this.peer.oniceconnectionstatechange = (): void => {
      const { iceConnectionState } = this.peer ?? {};
      this.info('Publish ICE state change', { iceConnectionState, peerTime: this.peerConnectionTime });
    };

    this.peer.onicegatheringstatechange = (): void => {
      const { iceGatheringState } = this.peer ?? {};
      this.info('Publish ICE gathering state change', { iceGatheringState, peerTime: this.peerConnectionTime });
    };

    this.peer.onconnectionstatechange = () => {
      // Peer may have already been destroyed. There's nothing to do in this case.
      if (!this.peer) {
        return;
      }
      const { connectionState } = this.peer;
      this.info('Publish state change', { connectionState });
      switch (connectionState) {
        case 'connected':
          break;

        case 'failed':
          this.handleConnectionFailed();
          break;

        default:
          break;
      }
    };

    const videoTracks = stream.getVideoTracks();
    const audioTracks = stream.getAudioTracks();
    const svc = this.videoRoomService.getSvcSettings();

    let codecs: RTCRtpCodecCapability[] | undefined;
    codecs = RTCRtpReceiver.getCapabilities('video')?.codecs;

    // TODO: Deprecated flag?
    if (this.codecSetting.mimeType && !svc) {
      const { sdpFormatFilter } = this.codecSetting;
      const formatFilter = sdpFormatFilter !== '' ? sdpFormatFilter : undefined;
      codecs = isUndefined(codecs) ? undefined : filterCodecs(codecs, this.codecSetting.mimeType, formatFilter);
    }

    const sendEncodings: RTCRtpEncodingParameters[] | undefined = svc
      ? [{ scalabilityMode: svc.shareScalabilityMode } as RTCRtpEncodingParameters]
      : undefined;

    this.info('Publishing video', { codecs, sendEncodings });

    const transceiver = this.peer?.addTransceiver(videoTracks[0], {
      direction: 'sendonly',
      sendEncodings,
    });

    // add audio tracks
    if (audioTracks.length > 0) {
      this.peer?.addTransceiver(audioTracks[0], {
        direction: 'sendonly',
      });
      for (const track of audioTracks) {
        if (!this.peer?.getSenders().find((sender) => sender.track === track)) {
          this.peer?.addTrack(track, stream);
        }
      }
    }
    // setCodecPreferences is not compatible with Firefox
    // https://caniuse.com/?search=setCodecPreferences
    if (codecs && transceiver && !this.browserService.isFirefox) {
      transceiver.setCodecPreferences(codecs);
    }

    const offer = await this.peer.createOffer();
    await this.peer.setLocalDescription(offer);

    const { sdp, type } = offer;
    if (!type || !sdp) {
      this.error('Got unexpected undefined offer value', offer);
      return;
    }

    // Send Offer
    const msg: StartSharingMessage = {
      t: OutgoingMessageType.SHR,
      offer: { type, sdp },
      bitrate: this.enhanced ? this.settings.enhanced.bitrate : this.settings.basic.bitrate,
    };
    this.websocketService.sendMessage(msg);
    this.publisherCandidates.forEach((c) => this.websocketService.sendMessage(c));
  }

  private publishScreenShareCandidate(event: RTCPeerConnectionIceEvent): void {
    const { candidate } = event;
    if (!candidate) {
      this.info('ICE Candidate was null, done');
      return;
    }

    assertDefined(this.user?.userId);
    const { userId } = this.user;

    this.info('ICE Candidate received', candidate.toJSON());

    const msg: SharingPublishCandidateMessage = {
      t: OutgoingMessageType.SHRCAN,
      userId,
      d: candidate as PeerCandidate,
    };
    if (this.websocketService.isConnected) {
      this.websocketService.sendMessage(msg);
    } else {
      this.publisherCandidates.push(msg);
    }
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private handleConnectionFailed(): void {
    this.rtcStatsStreamService.incrementCounter(StatsCounter.SCREENSHARE_PUBLISHER_CONNECTION_FAILED);
    const oneMinuteAgo = moment().subtract(1, 'minute');
    const connectionTime = this.peerConnectionTime;

    // If the connection fails in under a minute, or we never connected, advance the reconnection state machine to
    // potentially more reliable methods
    if (!connectionTime || connectionTime > oneMinuteAgo.valueOf()) {
      this.videoRoomService.advanceReconnectionState({ connectionTime });
    }
  }

  public get peer(): RTCPeerConnection | undefined {
    return this._peer;
  }

  private set peer(newPeer: RTCPeerConnection | undefined) {
    if (newPeer) {
      this.rtcStatsStreamService.addPublishMonitoring(PublisherType.SCREEN_SHARE, newPeer);
    } else {
      this.rtcStatsStreamService.removePublishMonitoring(PublisherType.SCREEN_SHARE);
    }
    this._peer = newPeer;
  }

  private stopPictureInPicture() {
    if (document.pictureInPictureElement) {
      document.exitPictureInPicture().catch((err) => new Error(err));
    }
  }
}
